/* eslint-disable import/no-anonymous-default-export */
import {
  DataProvider,
  fetchUtils,
  RaRecord,
  GetOneParams,
  GetManyParams,
  CreateParams,
  UpdateParams,
  UpdateManyParams,
  DeleteParams,
  DeleteManyParams,
  HttpError,
} from "react-admin";
import simpleRestProvider from "ra-data-simple-rest";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const getToken = () => localStorage.getItem("kc_token");

// 1️⃣ httpClient catches 400s and rethrows a HttpError with body=fieldErrors
const httpClient = async (url: string, options: any = {}) => {
  options.headers = options.headers || new Headers();
  const token = getToken();
  if (token) options.headers.set("Authorization", `Bearer ${token}`);

  try {
    return await fetchUtils.fetchJson(url, options);
  } catch (error: any) {
    const { status, body } = error;
    // If API returned field‐level validation errors:
    if (
      status === 400 &&
      body &&
      typeof body === "object" &&
      body.message &&
      typeof body.message === "object"
    ) {
      // body.message is { field: { errorName: errorMessage } }
      throw new HttpError(
        JSON.stringify(body.message) || "Validation error",
        status
      );
    }
    // otherwise rethrow
    throw error;
  }
};

// 2️⃣ Base provider uses our httpClient to add Bearer + error handling
const baseProvider = simpleRestProvider(API_URL, httpClient);

// 3️⃣ Export full DataProvider with all overrides (_id → id, search, etc.)
const dataProvider: DataProvider = {
  ...baseProvider,
  // GET LIST → POST /<resource>:search
  getList: async <RecordType extends RaRecord = any>(
    resource: string,
    params: any
  ): Promise<{ data: RecordType[]; total: number }> => {
    const { page, perPage } = params.pagination;
    const start = (page - 1) * perPage;
    const length = perPage;

    let { field: column, order: dir } = params.sort as any;
    if (column === "id") column = "_id";

    const rawFilters = (params.filter ?? {}) as Record<string, any>;
    const filterTree = rawFilters.filterTree
      ? { ...rawFilters.filterTree }
      : { operator: "and", operands: [] as any[] };

    // WORKAROUND :S
    for (const key of Object.keys(rawFilters)) {
      if (["filterTree", "preFilter", "populate"].includes(key)) continue;
      let value = rawFilters[key];
      if (value === undefined || value === null || value === "") continue;
      let filterType = "like";

      if (["client", "provider", "outlet"].includes(key)) {
        filterType = "eq";
      } else if (
        [
          "startDate",
          "startDateProvisional",
          "startDateInventory",
          "startDateLastOrderDate",
          "startDateCreatedAt",
          "startDateScheduled",
          "startDateExecution",
        ].includes(key)
      ) {
        filterType = "gte";
      } else if (
        [
          "endDate",
          "endDateProvisional",
          "endDateInventory",
          "endDateLastOrderDate",
          "endDateLastCreatedAt",
          "endDateLastScheduled",
          "endDateLastExecution",
        ].includes(key)
      ) {
        filterType = "lte";
      } else if (
        ["airbagsCount", "fullBinsCount", "emptyBinsCount"].includes(key)
      ) {
        value = value.toString();
        filterType = "gte";
      }

      filterTree.operands.push({
        term: {
          name: key,
          filterType,
          value: value,
        },
      });
    }

    const projection = (params.filter as any)?.projection;
    let populate: any = [];

    if (params.meta?.populate) {
      populate = params.meta.populate;
    }

    let preFilter: any[] = [];

    if (params.meta?.preFilter) {
      try {
        preFilter = JSON.parse(params.meta.preFilter);
      } catch {
        preFilter = [];
      }
    }

    const body: any = {
      start,
      length,
      order: { column, dir },
      ...(projection && { projection }),
      ...(populate && { populate }),
      preFilter,
      filter: filterTree,
    };

    const url = `${API_URL}/${resource}:search`;
    const { json } = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(body),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    const data = (json.items as any[])?.map((item) => ({
      ...item,
      id: item._id,
    })) as RecordType[];

    // <-- use the API’s recordsTotal (or recordsFiltered) here
    const total = json.recordsTotal ?? json.recordsFiltered;
    return { data, total };
  },

  // GET ONE → GET /<resource>/:id
  getOne: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetOneParams<RecordType>
  ) => {
    let populate: any = [];
    let populateParams = "";
    if (params.meta?.populate) {
      populate = params.meta.populate;
      populateParams = `?populate=${encodeURIComponent(populate)}`;
    }

    const url = `${API_URL}/${resource}/${params.id}${populateParams}`;

    const { json } = await httpClient(url);
    const rec = json.data;
    return { data: { ...(rec as object), id: rec._id } as RecordType };
  },

  // GET MANY → GET /<resource>?id=...
  getMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyParams<RecordType>
  ) => {
    const records = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${resource}/${id}`).then(({ json }) => {
          const rec = json.data;
          return {
            ...rec,
            id: rec._id,
          } as RecordType;
        })
      )
    );
    return { data: records };
  },

  // GET MANY REFERENCE → POST /<resource>:search + target filter
  getManyReference: async <RecordType extends RaRecord = any>(
    resource: string,
    params: any
  ) => {
    const { page, perPage } = params.pagination;
    const start = (page - 1) * perPage;
    const length = perPage;

    let { field: column, order: dir } = params.sort as any;
    if (column === "id") column = "_id";

    const baseTree = (params.filter as any)?.filterTree ?? {
      operator: "and",
      operands: [],
    };
    const refOperand = {
      term: { name: params.target, filterType: "eq", value: params.id },
    };
    const filterTree = {
      operator: "and",
      operands: [...baseTree.operands, refOperand],
    };
    const projection = (params.filter as any)?.projection;
    const populate = (params.filter as any)?.populate;

    const body: any = {
      start,
      length,
      order: { column, dir },
      ...(projection && { projection }),
      ...(populate && { populate }),
      preFilter: [],
      filter: filterTree,
    };

    const url = `${API_URL}/${resource}:search`;
    const { json } = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(body),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    const data = (json.items as any[]).map((item) => ({
      ...item,
      id: item._id,
    })) as RecordType[];
    return { data, total: json.total };
  },

  // CREATE → POST /<resource>/
  create: async <RecordType extends RaRecord = any>(
    resource: string,
    params: CreateParams<RecordType>
  ) => {
    const url = `${API_URL}/${resource}`;
    const { json } = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(params.data),
      headers: new Headers({ "Content-Type": "application/json" }),
    });
    const rec = json.data;
    return { data: { ...(rec as object), id: rec._id } as RecordType };
  },

  // UPDATE → PUT /<resource>/:id
  update: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateParams<RecordType>
  ) => {
    const url = `${API_URL}/${resource}/${params.id}`;
    const { json } = await httpClient(url, {
      method: "PUT",
      body: JSON.stringify(params.data),
      headers: new Headers({ "Content-Type": "application/json" }),
    });
    const rec = json.data;
    return { data: { ...(rec as object), id: rec._id } as RecordType };
  },

  // UPDATE MANY → multiple PUT, return ids
  updateMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateManyParams<RecordType>
  ) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${resource}/${id}`, {
          method: "PUT",
          body: JSON.stringify(params.data),
          headers: new Headers({ "Content-Type": "application/json" }),
        })
      )
    );
    return { data: params.ids };
  },

  // DELETE → DELETE /<resource>/:id
  delete: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteParams<RecordType>
  ) => {
    const url = `${API_URL}/${resource}/${params.id}`;
    const { json } = await httpClient(url, { method: "DELETE" });
    const rec = json.data;
    return { data: { ...(rec as object), id: rec._id } as RecordType };
  },

  // DELETE MANY → multiple DELETE
  deleteMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteManyParams<RecordType>
  ) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${resource}/${id}`, { method: "DELETE" })
      )
    );
    return { data: params.ids };
  },

  getInviteUrl: async (resource: string, params: any) => {
    const url = `${API_URL}/${resource}/${params.id}/invite-url`;
    const { json } = await httpClient(url, {
      method: "GET",
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    return json.data;
  },

  importSitesEquipments: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);

    const { json } = await httpClient(`${API_URL}/sites/import`, {
      method: "POST",
      body: fd,
    });

    return { data: json };
  },

  getSiteSortingRate: async (
    siteId: string,
    opts?: { from?: string; to?: string; inviteToken?: string }
  ) => {
    const qsParts: string[] = [];
    if (opts?.from) qsParts.push(`from=${encodeURIComponent(opts.from)}`);
    if (opts?.to) qsParts.push(`to=${encodeURIComponent(opts.to)}`);
    if (opts?.inviteToken)
      qsParts.push(`inviteToken=${encodeURIComponent(opts.inviteToken)}`);

    const qsStr = qsParts.length ? `?${qsParts.join("&")}` : "";

    const url = `${API_URL}/sites/${siteId}/sorting-rate${qsStr}`;

    const { json } = await httpClient(url, {
      method: "GET",
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    return {
      data: json.data,
    };
  },
} as typeof baseProvider;

export default dataProvider;
